import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  Sankey,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import { Network, SlidersHorizontal, Scale, TableProperties } from "lucide-react";
import {
  DashboardShell,
  PageHeading,
  ChartCard,
  LoadingPanel,
  fmt,
  fmtRca,
  tooltipStyle,
} from "@/components/DashboardShell";
import { useTradeData } from "@/components/DataProvider";
import { hsOpportunities } from "@/lib/analytics";
import { leadRca, perspectiveConfig } from "@/lib/perspective";
import type { Lead } from "@/lib/trade-types";

export const Route = createFileRoute("/opportunities")({
  head: () => ({ meta: [{ title: "Opportunity flows — Ready4Trade" }] }),
  component: OpportunitiesPage,
});

function OpportunitiesPage() {
  const { leads, aggregates, isLoading, perspective } = useTradeData();
  const [minRca, setMinRca] = useState(1);
  const [maxProducts, setMaxProducts] = useState(10);
  const config = perspectiveConfig(perspective);

  const opportunities = useMemo(
    () => (leads ? hsOpportunities(leads, perspective) : []),
    [leads, perspective],
  );
  const selected = useMemo(
    () => opportunities.filter((row) => (row.rca ?? 0) > minRca).slice(0, maxProducts),
    [maxProducts, minRca, opportunities],
  );
  const sankeyData = useMemo(
    () =>
      leads
        ? buildSankey(
            leads,
            perspective,
            selected.map((row) => row.code),
          )
        : { nodes: [], links: [] },
    [leads, perspective, selected],
  );
  const comparison = useMemo(
    () =>
      selected.map((row) => {
        const sample = leads?.find((lead) => lead.h6 === row.code);
        return {
          label: `${row.code} · ${trim(row.description, 22)}`,
          Uzbekistan: sample?.ru ?? 0,
          Slovakia: sample?.rs ?? 0,
        };
      }),
    [leads, selected],
  );

  if (isLoading || !leads || !aggregates)
    return (
      <DashboardShell>
        <LoadingPanel />
      </DashboardShell>
    );

  return (
    <DashboardShell meta={aggregates.meta}>
      <PageHeading
        title={`${config.flag} RCA-aligned opportunity flows`}
        subtitle="A deliberately filtered Sankey shows the strongest pathways from sector to HS6 product to buyer destination. It displays only top products and markets to avoid a noisy web of lines."
        badge={`${selected.length} products shown`}
      />

      <section className="surface-panel p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <SlidersHorizontal className="w-4 h-4 text-primary" /> Diagram controls
        </div>
        <label className="text-xs text-muted-foreground flex items-center gap-2">
          Minimum pooled RCA
          <select
            value={minRca}
            onChange={(event) => setMinRca(Number(event.target.value))}
            className="filter-select h-9"
          >
            <option value={1}>Above 1</option>
            <option value={2}>Above 2</option>
            <option value={5}>Above 5</option>
            <option value={10}>Above 10</option>
          </select>
        </label>
        <label className="text-xs text-muted-foreground flex items-center gap-2">
          Product nodes
          <select
            value={maxProducts}
            onChange={(event) => setMaxProducts(Number(event.target.value))}
            className="filter-select h-9"
          >
            <option value={6}>6 products</option>
            <option value={8}>8 products</option>
            <option value={10}>10 products</option>
            <option value={12}>12 products</option>
          </select>
        </label>
        <div className="ml-auto text-xs text-muted-foreground">
          Flow width = number of buyer leads
        </div>
      </section>

      <ChartCard
        title={`${config.country}: sector → HS6 → destination`}
        subtitle={`Top demand pathways where pooled RCA is above ${minRca}. Lesser destinations are grouped into “Other markets”.`}
        icon={<Network className="w-4 h-4" />}
      >
        {sankeyData.links.length ? (
          <div className="overflow-x-auto">
            <div className="min-w-[940px] h-[620px]">
              <ResponsiveContainer width="100%" height="100%">
                <Sankey
                  data={sankeyData}
                  node={<SankeyNode />}
                  nodePadding={22}
                  nodeWidth={12}
                  linkCurvature={0.52}
                  margin={{ top: 24, right: 175, bottom: 24, left: 150 }}
                  link={{ stroke: "#94a3b8", strokeOpacity: 0.24 }}
                >
                  <Tooltip content={<SankeyTooltip />} />
                </Sankey>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">
            No flows match the selected RCA threshold.
          </div>
        )}
      </ChartCard>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard
          title="Uzbekistan–Slovakia RCA comparison"
          subtitle="The same top demand products compared across both exporter perspectives"
          icon={<Scale className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={440}>
            <BarChart data={comparison} layout="vertical" margin={{ left: 10, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, "auto"]}
                tick={{ fontSize: 10, fill: "#64748b" }}
                stroke="#cbd5e1"
              />
              <YAxis
                dataKey="label"
                type="category"
                width={220}
                tick={{ fontSize: 10, fill: "#334155" }}
                stroke="#cbd5e1"
              />
              <Tooltip contentStyle={tooltipStyle} formatter={(value) => fmtRca(Number(value))} />
              <Legend />
              <Bar dataKey="Uzbekistan" fill="#10b981" radius={[0, 5, 5, 0]} />
              <Bar dataKey="Slovakia" fill="#2563eb" radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Opportunity shortlist"
          subtitle="Demand, comparative advantage and market breadth in one table"
          icon={<TableProperties className="w-4 h-4" />}
        >
          <div className="overflow-auto max-h-[440px] rounded-xl border border-border">
            <table className="w-full text-xs">
              <thead className="bg-secondary text-muted-foreground sticky top-0">
                <tr>
                  <Th>HS6</Th>
                  <Th>Product</Th>
                  <Th className="text-right">RCA</Th>
                  <Th className="text-right">Leads</Th>
                  <Th className="text-right">Markets</Th>
                </tr>
              </thead>
              <tbody>
                {selected.map((row) => (
                  <tr key={row.code} className="border-t border-border hover:bg-secondary/40">
                    <td className="px-3 py-2 font-mono font-semibold text-primary">{row.code}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.description}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {row.sector} · top: {row.topMarket}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">
                      {fmtRca(row.rca)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(row.count)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(row.destinations)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      </section>
    </DashboardShell>
  );
}

function buildSankey(leads: Lead[], perspective: "UZB" | "SVK", hsCodes: string[]) {
  const hsSet = new Set(hsCodes);
  const filtered = leads.filter(
    (lead) =>
      hsSet.has(lead.h6) &&
      (leadRca(lead, perspective) ?? 0) > 1 &&
      lead.dc &&
      lead.dc !== "Unknown",
  );
  const marketCounts = new Map<string, number>();
  filtered.forEach((lead) => marketCounts.set(lead.dc, (marketCounts.get(lead.dc) || 0) + 1));
  const topMarkets = new Set(
    [...marketCounts]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 9)
      .map(([market]) => market),
  );

  const sectors = [...new Set(filtered.map((lead) => lead.sec))];
  const hs = hsCodes
    .map((code) => {
      const lead = filtered.find((item) => item.h6 === code);
      return { code, label: `${code} · ${trim(lead?.hd || lead?.p || "Product", 38)}` };
    })
    .filter((item) => filtered.some((lead) => lead.h6 === item.code));
  const markets = [...topMarkets, "Other markets"];

  const nodes = [
    ...sectors.map((name) => ({ name, group: "sector" })),
    ...hs.map((item) => ({ name: item.label, group: "hs", code: item.code })),
    ...markets.map((name) => ({ name, group: "market" })),
  ];
  const index = new Map(nodes.map((node, i) => [node.name, i]));
  const links = new Map<string, { source: number; target: number; value: number }>();

  for (const lead of filtered) {
    const hsNode = hs.find((item) => item.code === lead.h6)?.label;
    if (!hsNode) continue;
    const market = topMarkets.has(lead.dc) ? lead.dc : "Other markets";
    addLink(links, index.get(lead.sec)!, index.get(hsNode)!);
    addLink(links, index.get(hsNode)!, index.get(market)!);
  }
  return { nodes, links: [...links.values()] };
}

function addLink(
  map: Map<string, { source: number; target: number; value: number }>,
  source: number,
  target: number,
) {
  const key = `${source}|${target}`;
  const current = map.get(key) ?? { source, target, value: 0 };
  current.value += 1;
  map.set(key, current);
}

interface SankeyNodeRenderProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: { group?: string; name?: string };
}

interface SankeyTooltipRenderProps {
  active?: boolean;
  payload?: Array<{
    payload?: {
      source?: { name?: string };
      target?: { name?: string };
      value?: number;
    };
  }>;
}

function SankeyNode(props: SankeyNodeRenderProps) {
  const { x = 0, y = 0, width = 0, height = 0, payload } = props;
  const group = payload?.group;
  const fill = group === "sector" ? "#10b981" : group === "hs" ? "#2563eb" : "#06b6d4";
  const right = group !== "sector";
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={Math.max(4, height)}
        rx={2}
        fill={fill}
        fillOpacity={0.9}
      />
      <text
        x={right ? x + width + 7 : x - 7}
        y={y + height / 2}
        textAnchor={right ? "start" : "end"}
        dominantBaseline="middle"
        fontSize={10}
        fill="#334155"
      >
        {payload?.name}
      </text>
    </g>
  );
}

function SankeyTooltip({ active, payload }: SankeyTooltipRenderProps) {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  return (
    <div style={tooltipStyle} className="px-3 py-2">
      <div className="font-semibold">
        {item?.source?.name} → {item?.target?.name}
      </div>
      <div className="text-muted-foreground">{fmt(item?.value)} buyer leads</div>
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-3 py-2 text-[10px] uppercase font-semibold tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function trim(value: string, length: number) {
  return value.length > length ? `${value.slice(0, length - 1)}…` : value;
}
